import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errCode } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { clearMasterCache } from "../../lib/master";
import { PageTitle } from "../../components/Common";
import { Trash2 } from "lucide-react";

const SPEC = {
  growers: [["name", "text"], ["is_test", "bool"]],
  farms: [["grower_id", "growers"], ["name", "text"], ["is_test", "bool"]],
  csgs: [["farm_id", "farms"], ["code", "text"], ["name", "text"]],
  sectors: [["farm_id", "farms"], ["csg_id", "csgs"], ["name", "text"], ["code", "text"]],
  varieties: [["name", "text"]],
  contractors: [["grower_id", "growers"], ["code", "text"], ["name", "text"]],
  crews: [["contractor_id", "contractors"], ["farm_id", "farms"], ["name", "text"]],
  bin_types: [["name", "text"], ["tare_kg", "number"]],
  seasons: [["name", "text"], ["start_date", "date"], ["end_date", "date"], ["active", "bool"]],
};

const REQ = { growers: ["name"], farms: ["grower_id", "name"], csgs: ["farm_id", "code"], sectors: ["farm_id", "name"], varieties: ["name"],
  contractors: ["grower_id", "code"], crews: ["contractor_id", "farm_id", "name"], bin_types: ["name"], seasons: ["name", "start_date", "end_date"] };
const FIELD_KEY = { grower_id: "grower", farm_id: "farm", csg_id: "csg", contractor_id: "contractor", name: "name", is_test: "test_flag",
  tare_kg: "tare", start_date: "start", end_date: "end", active: "active" };
const CODE_KEY = { csgs: "code_csg", sectors: "code_sector", contractors: "code_contractor" };
const fieldLabel = (t, kind, n) => (n === "code" ? t(CODE_KEY[kind]) : t(FIELD_KEY[n] || n));

const FieldInput = ({ name, type, value, onChange, refs }) => {
  if (type === "bool") return <input type="checkbox" className="w-6 h-6" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={`master-field-${name}`} />;
  if (SPEC[type]) return (
    <select className="field !min-h-[44px]" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`master-field-${name}`}>
      <option value="">—</option>{(refs[type] || []).map((o) => <option key={o.id} value={o.id}>{o.name || o.code}</option>)}</select>);
  return <input className="field !min-h-[44px]" type={type} step="0.01" value={value ?? ""} onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} data-testid={`master-field-${name}`} />;
};

export default function Master() {
  const { t, te } = useLang();
  const [sp] = useSearchParams();
  const [kind, setKind] = useState(SPEC[sp.get("kind")] ? sp.get("kind") : "growers");
  useEffect(() => { if (SPEC[sp.get("kind")]) setKind(sp.get("kind")); }, [sp]);
  const [data, setData] = useState({});
  const [f, setF] = useState({});
  const load = () => Promise.all(Object.keys(SPEC).map((k) => api.get(`/master/${k}`).then((r) => [k, r.data]))).then((e) => setData(Object.fromEntries(e)));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (f.id) await api.patch(`/master/${kind}/${f.id}`, f); else await api.post(`/master/${kind}`, f);
      toast.success(t("saved")); setF({}); clearMasterCache(); load();
    } catch (e) { toast.error(te(errCode(e))); }
  };
  const missing = REQ[kind].filter((k) => f[k] === undefined || f[k] === null || String(f[k]).trim() === "");
  const remove = async (row) => {
    if (!window.confirm(`${t("delete_confirm")} "${row.name || row.code}"?`)) return;
    try { await api.delete(`/master/${kind}/${row.id}`); toast.success(t("deleted")); if (f.id === row.id) setF({}); clearMasterCache(); load(); }
    catch (e) {
      const c = errCode(e);
      if (c.startsWith("in_use:")) toast.error(`${t("in_use")} (${c.slice(7)})`, { duration: 6000 }); else toast.error(te(c));
    }
  };
  const label = (k, v) => typeof v === "boolean" ? (v ? t("yes") : t("no")) : (SPEC[SPEC[kind].find((x) => x[0] === k)?.[1]] ? (data[SPEC[kind].find((x) => x[0] === k)[1]] || []).find((o) => o.id === v)?.name || "—" : String(v ?? "—"));

  return (
    <div data-testid="master-page">
      <PageTitle>{t("nav_master")}</PageTitle>
      <div className="flex flex-wrap gap-2 mb-4">{Object.keys(SPEC).map((k) => (
        <button key={k} onClick={() => { setKind(k); setF({}); }} data-testid={`master-tab-${k}`}
          className={`px-3 py-2 rounded-lg text-sm font-bold border-2 ${kind === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300"}`}>{t(`kind_${k}`)}</button>))}</div>
      {CODE_KEY[kind] && <p className="text-sm text-slate-600 mb-3" data-testid="master-code-hint">{t(`hint_${kind}`)}</p>}
      <div className="card p-4 mb-4">
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          {SPEC[kind].map(([name, type]) => (
            <label key={name} className="block"><span className="eyebrow">{fieldLabel(t, kind, name)}{REQ[kind].includes(name) ? " *" : ""}{name === "tare_kg" ? ` ${t("tare_blank")}` : ""}</span>
              <div className="mt-1"><FieldInput name={name} type={type} value={f[name]} refs={data} onChange={(v) => setF({ ...f, [name]: v })} /></div></label>))}
          <div className="flex gap-2"><button className="btn-primary !min-h-[44px]" disabled={missing.length > 0} onClick={save} data-testid="master-save-button">{f.id ? t("save") : t("add")}</button>
            {f.id && <button className="btn-secondary !min-h-[44px]" onClick={() => setF({})}>{t("cancel")}</button>}</div>
        </div>
      </div>
      <div className="card overflow-x-auto">
        <table className="tbl w-full" data-testid="master-table">
          <thead><tr>{SPEC[kind].map(([n]) => <th key={n}>{fieldLabel(t, kind, n)}</th>)}<th></th></tr></thead>
          <tbody>{(data[kind] || []).map((r) => (
            <tr key={r.id}>{SPEC[kind].map(([n]) => <td key={n}>{label(n, r[n])}</td>)}
              <td className="whitespace-nowrap space-x-3"><button className="text-sm underline" onClick={() => setF(r)} data-testid={`master-edit-${r.id}`}>{t("edit")}</button>
                <button className="text-sm underline text-red-700 inline-flex items-center gap-1" onClick={() => remove(r)} data-testid={`master-delete-${r.id}`}><Trash2 size={14} />{t("delete")}</button></td></tr>))}
            {!(data[kind] || []).length && <tr><td colSpan={SPEC[kind].length + 1} className="text-slate-500">{t("none")}</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}
